import React, { type ReactNode } from "react";
import { Text } from "@chakra-ui/react";
import { type Theme } from "contexts/color-theme";
import { remCalc } from "theme/utils";

export type StatisticProps = {
  /**
   * The color theme to use for text colors.
   *
   * @default "light"
   */
  theme?: Theme;

  /**
   * The size of the statistic.
   *
   * @default "lg"
   */
  size?: "sm" | "md" | "lg";

  /**
   * The title of the statistic.
   */
  title: ReactNode;

  /**
   * The description of the statistic.
   */
  description: ReactNode;
};

export function Statistic({ title, description, size = "lg", theme = "light" }: StatisticProps) {
  let titleTextStyle;
  let descriptionTextStyle;
  let descriptionMb;
  switch (size) {
    case "sm": {
      titleTextStyle = { base: "headline3", md: "headline2" };
      descriptionTextStyle = { base: "overline2", md: "overline1" };
      descriptionMb = { base: remCalc(-1), md: "tinier" };
      break;
    }

    case "md": {
      titleTextStyle = { base: "headline1", md: "display2" };
      descriptionTextStyle = { base: "overline2", md: "overline1" };
      descriptionMb = { base: remCalc(1), md: "tiny" };
      break;
    }

    default: {
      titleTextStyle = { base: "display3", lg: "display1" };
      descriptionTextStyle = "overline1";
      descriptionMb = { base: remCalc(3), lg: "tiny" };
      break;
    }
  }

  return (
    <Text display="flex" alignItems="end" gap={{ base: "smallest", sm: "smaller" }}>
      <Text as="span" color={`brand.text.${theme}`} flexShrink={0} lineHeight="none" textStyle={titleTextStyle}>
        {title}
      </Text>

      <Text
        as="span"
        textAlign="start"
        color={`brand.textSecondary.${theme}`}
        textStyle={descriptionTextStyle}
        // Bottom margin is used to align the description with the bottom of the title
        mb={descriptionMb}
      >
        {description}
      </Text>
    </Text>
  );
}
